import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingBag, Check } from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatPrice } from '@/lib/utils';
import ProductImage from '@/components/ProductImage';
import { useWishlist } from '@/contexts/WishlistContext';
import { useCart } from '@/contexts/CartContext';
import { useCartLine } from '@/hooks/useCartLine';
import CartStepper from '@/components/CartStepper';
import SaleEndsBadge from '@/components/SaleEndsBadge';
import { useVariantSelection } from '@/hooks/useVariantSelection';

// دوائر الألوان (قابلة للاختيار)
const ColorOption = ({ option, selectedId, onSelect }) => (
  <div className="flex flex-wrap items-center gap-2" role="radiogroup" aria-label={option.name}>
    {option.values.map((value) => {
      const isSelected = selectedId === value.id;
      return (
        <button
          key={value.id}
          type="button"
          role="radio"
          aria-checked={isSelected}
          aria-label={value.value}
          title={value.value}
          onClick={() => onSelect(option.attribute_id, value.id)}
          className={cn(
            'relative w-5 h-5 md:w-6 md:h-6 rounded-full ring-offset-2 ring-offset-white shadow-sm transition-all duration-200',
            isSelected ? 'ring-2 ring-burgundy-500 scale-110' : 'ring-1 ring-black/15 hover:scale-110'
          )}
          style={{ backgroundColor: value.hex || '#E5E7EB' }}
        />
      );
    })}
  </div>
);

// باقي الخصائص (لو فيه) كأزرار صغيرة
const ChipOption = ({ option, selectedId, onSelect }) => (
  <div className="flex flex-wrap items-center gap-1.5" role="radiogroup" aria-label={option.name}>
    {option.values.map((value) => {
      const isSelected = selectedId === value.id;
      return (
        <button
          key={value.id}
          type="button"
          role="radio"
          aria-checked={isSelected}
          title={`${option.name}: ${value.value}`}
          onClick={() => onSelect(option.attribute_id, value.id)}
          className={cn(
            'px-2 py-0.5 rounded-full border text-[11px] md:text-xs font-cairo font-semibold whitespace-nowrap transition-colors',
            isSelected
              ? 'border-burgundy-500 bg-burgundy-500 text-white'
              : 'border-gray-200 text-gray-600 hover:border-burgundy-500 hover:text-burgundy-500'
          )}
        >
          {value.value}
        </button>
      );
    })}
  </div>
);

/**
 * initialVariantId: يفتح الكارت على تركيبة معينة (مثلاً تركيبة محفوظة في المفضلة).
 */
const ProductCard = ({ product, initialVariantId = null }) => {
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { cart } = useCart();

  // لو فيه تركيبة من المنتج في السلة، الكارت يفتح عليها (مرة واحدة أول ما السلة تتحمّل)
  const productLines = (cart.items || []).filter((item) => item.product_id === product.id);
  const productQuantity = productLines.reduce((sum, item) => sum + item.quantity, 0);
  const [cartVariantId, setCartVariantId] = useState(null);
  useEffect(() => {
    if (!cartVariantId && productLines[0]) setCartVariantId(productLines[0].variant_id);
  }, [productLines[0]?.variant_id]);

  const { options, selected, selectedVariant, selectedLabel, selectValue } = useVariantSelection(
    product,
    initialVariantId ?? cartVariantId
  );
  const line = useCartLine(selectedVariant?.id);
  // المفضلة والسلة على مستوى التركيبة المختارة (لون + خصائص)
  const liked = isInWishlist(selectedVariant?.id);
  const productUrl = selectedVariant ? `/products/${product.id}?variant=${selectedVariant.id}` : `/products/${product.id}`;

  // السعر حسب التركيبة المختارة (price_override) وإلا سعر المنتج
  const price = selectedVariant?.price ?? product.price;
  const originalPrice = product.original_price && product.original_price > price ? product.original_price : null;
  const canAdd = Boolean(selectedVariant);

  const handleWishlist = async () => {
    if (!selectedVariant) {
      toast.error('الرجاء اختيار اللون أولاً');
      return;
    }
    try {
      const added = await toggleWishlist(product, selectedVariant.id, selectedLabel);
      toast.success(added ? 'تمت الإضافة إلى المفضلة' : 'تمت الإزالة من المفضلة', { description: selectedLabel });
    } catch (error) {
      toast.error('تعذر تحديث المفضلة');
    }
  };

  const handleAddToCart = async () => {
    if (!canAdd) {
      toast.error('الرجاء اختيار اللون أولاً');
      return;
    }

    if (await line.add(1)) {
      toast.success(`تمت إضافة ${product.name_ar} إلى السلة`, { description: selectedLabel });
    }
  };

  return (
    <div className="group relative flex flex-col h-full bg-white rounded-2xl border border-gray-200/80 overflow-hidden shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-burgundy-500/40">
      <div className="relative aspect-[3/4] overflow-hidden bg-gray-100">
        <Link to={productUrl} tabIndex={-1} aria-hidden="true">
          <ProductImage
            src={product.images?.[0]?.url}
            name={product.name_ar}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>

        {originalPrice && (
          <span className="absolute top-3 right-3 bg-destructive text-white text-xs font-bold px-3 py-1 rounded-full shadow">
            -{Math.round((1 - price / originalPrice) * 100)}%
          </span>
        )}
        {product.is_new && !originalPrice && (
          <span className="absolute top-3 right-3 bg-brand-gold text-brand-black text-xs font-bold px-3 py-1 rounded-full shadow">
            جديد
          </span>
        )}

        <button
          type="button"
          onClick={handleWishlist}
          aria-label={liked ? 'إزالة من المفضلة' : 'إضافة إلى المفضلة'}
          aria-pressed={liked}
          className={cn(
            'absolute top-3 left-3 w-9 h-9 rounded-full flex items-center justify-center bg-white/90 backdrop-blur shadow-md transition-all duration-200 hover:scale-110',
            liked ? 'text-destructive' : 'text-gray-600 hover:text-destructive'
          )}
        >
          <Heart className={cn('w-[18px] h-[18px]', liked && 'fill-current')} />
        </button>

        {originalPrice && product.sale_ends_at && (
          <SaleEndsBadge endsAt={product.sale_ends_at} className="absolute bottom-3 left-3" />
        )}

        {productQuantity > 0 && (
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 bg-emerald-600 text-white text-[11px] md:text-xs font-cairo font-bold pr-2 pl-2.5 py-1 rounded-full shadow-md animate-in fade-in zoom-in-95 duration-200">
            <Check className="w-3.5 h-3.5" strokeWidth={3} />
            في السلة{productQuantity > 1 ? ` (${productQuantity})` : ''}
          </span>
        )}
      </div>

      <div className="flex flex-col flex-1 gap-2 p-3 md:p-4">
        <Link to={productUrl} className="font-cairo font-semibold text-brand-black line-clamp-1 hover:text-burgundy-500 transition-colors">
          {product.name_ar}
        </Link>

        {options.length > 0 && (
          <div className="flex flex-col gap-2">
            {options.map((option) =>
              option.is_color ? (
                <ColorOption key={option.attribute_id} option={option} selectedId={selected[option.attribute_id]} onSelect={selectValue} />
              ) : (
                <ChipOption key={option.attribute_id} option={option} selectedId={selected[option.attribute_id]} onSelect={selectValue} />
              )
            )}
          </div>
        )}

        <div className="mt-auto pt-2 border-t border-gray-100 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-baseline gap-2 flex-wrap min-w-0">
              <p className="text-burgundy-500 font-bold text-base md:text-lg transition-all" aria-live="polite">
                {formatPrice(price)}
              </p>
              {originalPrice && (
                <p className="text-gray-400 line-through text-xs md:text-sm">{formatPrice(originalPrice)}</p>
              )}
            </div>
            {!line.inCart && (
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={line.busy || !canAdd}
                aria-label="أضف إلى السلة"
                title="أضف إلى السلة"
                className="shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center bg-burgundy-500 text-white shadow-md transition-all duration-200 hover:bg-burgundy-600 hover:scale-110 disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
              >
                <ShoppingBag className="w-4 h-4 md:w-5 md:h-5" />
              </button>
            )}
          </div>
          {line.inCart && (
            <CartStepper
              className="w-full animate-in fade-in slide-in-from-bottom-1 duration-200"
              quantity={line.quantity}
              label="في السلة"
              busy={line.busy}
              canIncrement={line.canIncrement}
              onIncrement={line.increment}
              onDecrement={line.decrement}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
